import React, { useState, useEffect, useRef, useCallback } from 'react';
import { uploadVideoChunk } from '../api/uploadService';

const CHECK_INTERVAL = 300;

const DeviceCheck = ({ onProceed }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const audioAnalyserRef = useRef(null);
  const audioDataRef = useRef(null);
  const animFrameRef = useRef(null);
  const micIntervalRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const preExamSessionIdRef = useRef(
    'pre-exam-' + Math.random().toString(36).substring(2) + Date.now().toString(36)
  );
  const recordedChunksRef = useRef([]);
  const recordIntervalRef = useRef(null);

  const [camStatus, setCamStatus] = useState('checking');
  const [micStatus, setMicStatus] = useState('checking');
  const [micLevel, setMicLevel] = useState(0);
  const [camReady, setCamReady] = useState(false);
  const [micReady, setMicReady] = useState(false);
  const [error, setError] = useState(null);

  const startPreExamRecording = useCallback((stream) => {
    let options = { mimeType: 'video/webm;codecs=vp9,opus' };
    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      options = { mimeType: 'video/webm;codecs=vp8,opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/webm' };
      }
    }
    const mr = new MediaRecorder(stream, options);
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        recordedChunksRef.current.push(e.data);
      }
    };
    mr.start();
    mediaRecorderRef.current = mr;
    recordIntervalRef.current = setInterval(() => {
      if (mr.state === 'recording') mr.requestData();
    }, 5000);
  }, []);

  const cleanup = useCallback(() => {
    if (recordIntervalRef.current) clearInterval(recordIntervalRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (micIntervalRef.current) clearInterval(micIntervalRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.style.transform = 'scaleX(-1)';
        }

        setCamStatus('ok');
        setCamReady(true);
        startPreExamRecording(stream);

        // Audio analyser
        try {
          const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 64;
          source.connect(analyser);
          audioAnalyserRef.current = analyser;
          audioDataRef.current = new Uint8Array(analyser.frequencyBinCount);

          let micActiveCount = 0;
          micIntervalRef.current = setInterval(() => {
            if (!audioAnalyserRef.current || !audioDataRef.current) return;
            audioAnalyserRef.current.getByteFrequencyData(audioDataRef.current);
            const avg = audioDataRef.current.reduce((a, b) => a + b, 0) / audioDataRef.current.length;
            setMicLevel(Math.min(avg / 128, 1));

            if (avg > 5) {
              micActiveCount++;
              if (micActiveCount > 3 && micStatus === 'checking') {
                setMicStatus('ok');
                setMicReady(true);
              }
            }
          }, CHECK_INTERVAL);

          setTimeout(() => {
            if (micStatus === 'checking') {
              setMicStatus('ok');
              setMicReady(true);
            }
          }, 2000);
        } catch (e) {
          setMicStatus('ok');
          setMicReady(true);
        }
      } catch (err) {
        setCamStatus('fail');
        setMicStatus('fail');
        setError(err.name === 'NotAllowedError'
          ? 'Camera and microphone access denied. Please allow permissions in your browser settings and try again.'
          : 'Could not access camera or microphone. Please check your devices.');
      }
    };

    init();

    return cleanup;
  }, [cleanup, micStatus]);

  const retry = () => {
    cleanup();
    setCamStatus('checking');
    setMicStatus('checking');
    setCamReady(false);
    setMicReady(false);
    setMicLevel(0);
    setError(null);
    setTimeout(() => {
      navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        .then(stream => {
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.style.transform = 'scaleX(-1)';
          }
          setCamStatus('ok');
          setCamReady(true);
          startPreExamRecording(stream);
          setMicStatus('ok');
          setMicReady(true);
        })
        .catch(() => {
          setCamStatus('fail');
          setMicStatus('fail');
          setError('Access still denied. Please update your browser permissions.');
        });
    }, 500);
  };

  const statusIcon = (status) => {
    if (status === 'checking') return <div style={styles.spinnerSmall} />;
    if (status === 'ok') return <span style={{ color: '#4cbb4c', fontSize: 20 }}>✅</span>;
    return <span style={{ color: '#d32f2f', fontSize: 20 }}>❌</span>;
  };

  const barColor = micLevel > 0.6 ? '#4cbb4c' : micLevel > 0.2 ? '#ff9800' : '#4cbb4c';

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.shieldIcon}>🎥</div>
        <h1 style={styles.title}>Device Check</h1>
        <p style={styles.desc}>
          Please verify your camera and microphone are working before entering the exam.
        </p>

        {error && (
          <div style={styles.errorBox}>
            <span style={{ fontSize: 16 }}>⚠️</span> {error}
          </div>
        )}

        <div style={styles.devicesRow}>
          <div style={styles.deviceCard}>
            <div style={styles.previewContainer}>
              <video ref={videoRef} autoPlay muted playsInline style={styles.previewVideo} />
            </div>
            <div style={styles.deviceStatus}>
              {statusIcon(camStatus)}
              <span style={{ marginLeft: 8, fontWeight: 600, fontSize: 14, color: camStatus === 'ok' ? '#2e7d32' : camStatus === 'fail' ? '#c62828' : '#888' }}>
                {camStatus === 'checking' ? 'Checking camera...' : camStatus === 'ok' ? 'Camera working' : 'Camera blocked'}
              </span>
            </div>
          </div>

          <div style={styles.deviceCard}>
            <div style={styles.micVisualizer}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎤</div>
              <div style={styles.micBarTrack}>
                <div style={{
                  ...styles.micBarFill,
                  width: `${Math.max(micLevel * 100, 8)}%`,
                  background: barColor
                }} />
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                {micStatus === 'checking' ? 'Detecting microphone...' : micStatus === 'ok' ? 'Microphone detected' : 'Microphone blocked'}
              </div>
            </div>
            <div style={styles.deviceStatus}>
              {statusIcon(micStatus)}
              <span style={{ marginLeft: 8, fontWeight: 600, fontSize: 14, color: micStatus === 'ok' ? '#2e7d32' : micStatus === 'fail' ? '#c62828' : '#888' }}>
                {micStatus === 'checking' ? 'Checking mic...' : micStatus === 'ok' ? 'Microphone working' : 'Microphone blocked'}
              </span>
            </div>
          </div>
        </div>

        <div style={styles.actions}>
          {error && (
            <button onClick={retry} style={styles.retryBtn}
              onMouseEnter={e => e.currentTarget.style.background = '#e65100'}
              onMouseLeave={e => e.currentTarget.style.background = '#f57c00'}
            >🔄 Retry</button>
          )}
          <button
            onClick={() => {
              const mr = mediaRecorderRef.current;
              if (mr && mr.state !== 'inactive') {
                mr.requestData();
                mr.stop();
              }
              const sid = preExamSessionIdRef.current;
              setTimeout(() => {
                recordedChunksRef.current.forEach(chunk => {
                  const fd = new FormData();
                  fd.append('chunk', chunk, 'chunk.webm');
                  fd.append('uploadSessionId', sid);
                  uploadVideoChunk(fd);
                });
              }, 200);
              cleanup();
              onProceed(sid);
            }}
            disabled={!camReady || !micReady}
            style={{
              ...styles.proceedBtn,
              opacity: (!camReady || !micReady) ? 0.5 : 1,
              cursor: (!camReady || !micReady) ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={e => { if (camReady && micReady) e.currentTarget.style.transform = 'scale(1.03)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {!camReady || !micReady ? 'Waiting for devices...' : 'Proceed to Exam →'}
          </button>
        </div>

        <p style={{ fontSize: 12, color: '#999', marginTop: 16 }}>
          Your camera and microphone will be recorded throughout the entire exam.
        </p>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(160deg, #0a0a0a 0%, #111827 50%, #0d1f12 100%)',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    padding: 24
  },
  card: {
    background: '#fff', borderRadius: 20, padding: '40px 36px',
    maxWidth: 600, width: '100%', textAlign: 'center',
    boxShadow: '0 25px 80px rgba(0,0,0,.3)'
  },
  shieldIcon: { fontSize: 48, marginBottom: 12 },
  title: { margin: '0 0 6px', fontSize: 26, fontWeight: 800, color: '#111' },
  desc: { margin: '0 0 24px', fontSize: 14, lineHeight: 1.6, color: '#666' },
  errorBox: {
    padding: '12px 16px', background: '#fdecea', borderRadius: 10,
    color: '#c62828', fontSize: 13, marginBottom: 20, textAlign: 'left',
    border: '1px solid #ef9a9a', lineHeight: 1.5
  },
  devicesRow: {
    display: 'flex', gap: 16, marginBottom: 24,
    flexWrap: 'wrap', justifyContent: 'center'
  },
  deviceCard: {
    flex: '1 1 220px', background: '#fafafa', borderRadius: 14,
    padding: 20, border: '1px solid #eee', textAlign: 'center'
  },
  previewContainer: {
    width: '100%', aspectRatio: '4/3', borderRadius: 10,
    overflow: 'hidden', background: '#000', marginBottom: 12
  },
  previewVideo: { width: '100%', height: '100%', objectFit: 'cover' },
  deviceStatus: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
  },
  micVisualizer: {
    padding: '20px 0 12px', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center'
  },
  micBarTrack: {
    width: '80%', height: 8, background: '#eee', borderRadius: 4,
    overflow: 'hidden'
  },
  micBarFill: {
    height: '100%', borderRadius: 4, transition: 'width .15s ease, background .3s'
  },
  actions: {
    display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap'
  },
  retryBtn: {
    padding: '12px 24px', borderRadius: 10, border: 'none',
    background: '#f57c00', color: '#fff', fontWeight: 700, fontSize: 15,
    cursor: 'pointer', transition: 'all .25s'
  },
  proceedBtn: {
    padding: '12px 32px', borderRadius: 10, border: 'none',
    background: 'linear-gradient(135deg,#4cbb4c,#2e9e2e)',
    color: '#fff', fontWeight: 700, fontSize: 16,
    transition: 'all .25s'
  },
  spinnerSmall: {
    width: 18, height: 18,
    border: '2px solid #ddd', borderTopColor: '#4cbb4c',
    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    display: 'inline-block'
  }
};

export default DeviceCheck;
